import React, { useState, useEffect, useCallback, useRef } from 'react';

import {
    Heading,
    Container,
    Row,
    Col,
    Text,
    className,
    isMobile,
    ModalBase,
} from 'aplazame-design-system';
import TransitionLink from 'aplazame-design-system/src/components/TransitionLink';
import webpIsSupported from 'aplazame-design-system/src/utils/webpIsSupported';
import { useTranslate } from 'soluble-gatsby-source';

import TabComponent from '../TabComponent';

import style from './style.module.scss';
import textStyle from 'aplazame-design-system/src/scss/text.module.scss';
import colorStyle from 'aplazame-design-system/src/scss/color.module.scss';
import sectionStyle from 'aplazame-design-system/src/scss/section.module.scss';

export default function HeaderSection({
    id,
    title,
    titleTag,
    subtitle,
    colorScheme,
    bgImage,
    bgImageWebp,
    showSubpagesMenu,
    subpages,
    pageUrl,
    pt,
    pb,
    wrapperClassName,
    subtitleClassName,
    tabItems,
    activeTabTitle,
    closeModalOnHashChange,
    tabItemsTitle,
    tabSectionPt,
    tabClassName,
    tabLinkClassName,
    tabLinkActiveClassName,
    extraContent,
    textSmaller,
}) {
    colorScheme = colorScheme?.toLowerCase();
    const translation = useTranslate();
    const modalRef = useRef();
    const [imageSrc, setImageSrc] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        if ((!bgImage && !bgImageWebp) || typeof window === 'undefined') {
            return;
        }

        webpIsSupported((webpSupport) => {
            const image =
                webpSupport &&
                    bgImageWebp &&
                    bgImageWebp.file &&
                    bgImageWebp.file.childImageSharp
                    ? bgImageWebp
                    : bgImage && bgImage.file && bgImage.file.childImageSharp
                        ? bgImage
                        : null;

            if (image) {
                const img = new Image();
                img.onload = () => {
                    setImageSrc(image.file.childImageSharp.resize.src);
                };
                img.src = image.file.childImageSharp.resize.src;
            }
        });
    }, [bgImage, bgImageWebp, setImageSrc]);

    const openTagsModal = useCallback(() => {
        if (modalRef && modalRef.current) {
            modalRef.current.toggle();
            setIsModalOpen(modalRef.current.state.sho);
            modalRef.current.modalRef.focus();
        }
    }, [modalRef]);

    useEffect(() => {
        const onFocusListener = (event) => {
            if (isModalOpen) {
                const dialogParent = event.target.closest('[role="dialog"]');
                if (dialogParent != modalRef.current.modalRef) {
                    if (modalRef.current.state.show) {
                        modalRef.current.toggle();
                    }
                }
            }
        };

        document.addEventListener('focusin', onFocusListener);

        return () => {
            document.removeEventListener('focusin', onFocusListener);
        };
    }, [isModalOpen]);

    useEffect(() => {
        if (!closeModalOnHashChange) {
            return;
        }

        const onHashChangeListener = () => {
            if (isMobile() && window.location.hash !== '#_') {
                openTagsModal();
            }
        };

        window.addEventListener('hashchange', onHashChangeListener);

        return () => {
            window.removeEventListener('hashchange', onHashChangeListener);
        };
    }, [closeModalOnHashChange, openTagsModal]);

    const getCategoryTabItems = useCallback(() => {
        return [
            {
                active: pageUrl ? true : false,
                title:
                    translation['header-section.categories-dropdown.home'].text,
                href: pageUrl ? pageUrl : '../',
            },
            ...subpages.map((s) => ({
                scheme: 'gatsby',
                title: s.title,
                href: s.slug,
                Tag: TransitionLink,
            })),
        ];
    }, [subpages, pageUrl, translation]);

    return (
        <>
            <section
                aria-labelledby={id}
                {...className(
                    [
                        style.section,
                        bgImage ? colorStyle['white-text'] : '',
                        sectionStyle['with-bg'],
                        sectionStyle[`with-bg--none`],
                        colorStyle[`with-bg--${colorScheme}`],
                        colorScheme && !bgImage
                            ? colorStyle[`bg--${colorScheme}`]
                            : '',
                        textSmaller ? '' : pageUrl ? 'mb-5' : '',
                        wrapperClassName ? wrapperClassName : '',
                    ].join(' ')
                )}
            >
                {bgImage || imageSrc ? (
                    <div
                        data-sal="fade"
                        data-sal-easing="ease-out-quart"
                        {...className(`${sectionStyle.bg} ${colorStyle.bg}`)}
                        style={{
                            '--sal-delay': `300ms`,
                            backgroundImage: imageSrc
                                ? `url("${imageSrc}")`
                                : bgImage && bgImage.file
                                    ? bgImage.file.childImageSharp
                                        ? `url("${bgImage.file.childImageSharp.resize.tracedSVG}")`
                                        : `url("${bgImage.file.publicURL}")`
                                    : 'none',
                        }}
                    />
                ) : null}
                <Container
                    pt={
                        textSmaller
                            ? { xs: 10 }
                            : pt
                                ? pt
                                : { xs: bgImageWebp ? 10 : 5, lg: 8 }
                    }
                    pb={textSmaller ? { xs: 10 } : pb ? pb : { xs: 15, lg: 8 }}
                >
                    <Row>
                        <Col
                            className="text-center"
                            col={{ lg: 8 }}
                            offset={{ lg: 2 }}
                        >
                            {title ? (
                                <Heading
                                    id={id}
                                    tag={titleTag}
                                    className={`mb-4 ${textSmaller
                                        ? 'h2 mb-lg-5'
                                        : 'h1 mb-lg-3'
                                        } ${textStyle.gradual} ${style.heading}`}
                                    data-sal="slide-up"
                                    data-sal-easing="ease-out-quart"
                                    style={{
                                        '--sal-delay': `300ms`,
                                    }}
                                >
                                    {title}
                                </Heading>
                            ) : null}
                            {subtitle ? (
                                <Text
                                    tag="div"
                                    className={`
                                        ${textSmaller
                                            ? 'h4demo'
                                            : subtitleClassName
                                                ? subtitleClassName
                                                : `h5`
                                        } ${style.text}`}
                                    data-sal="slide-up"
                                    data-sal-easing="ease-out-quart"
                                    style={{
                                        '--sal-delay': `600ms`,
                                    }}
                                >
                                    {subtitle}
                                </Text>
                            ) : null}
                        </Col>
                    </Row>
                </Container>
                {extraContent ? extraContent : null}
                {showSubpagesMenu &&
                    ((subpages && subpages.length) ||
                        (tabItems && tabItems.length)) ? (
                    <div
                        {...className(`${style['dropdown-wrapper']} d-lg-none`)}
                        data-sal="slide-up"
                        data-sal-easing="ease-out-quart"
                        style={{
                            '--sal-delay': extraContent ? '' : `900ms`,
                        }}
                    >
                        <button
                            {...className(`body pt-3 pb-3 ${style.dropdown}`)}
                            onClick={openTagsModal}
                        >
                            {activeTabTitle
                                ? activeTabTitle
                                : translation[
                                    'header-section.categories-dropdown.home'
                                ].text}
                        </button>
                    </div>
                ) : null}
            </section>
            {showSubpagesMenu &&
                ((subpages && subpages.length) || (tabItems && tabItems.length)) ? (
                <ModalBase
                    parentRef={modalRef}
                    wrapperClassName={style.modal}
                    closeButtonClassName={style['modal__close']}
                    bodyClassName={style['modal__body']}
                    title={tabItemsTitle ? tabItemsTitle : null}
                    titleClassName={`body ${style['modal__title']}`}
                >
                    {(subpages && subpages.length) ||
                        (tabItems && tabItems.length) ? (
                        <TabComponent
                            className={`${style.tabs} ${style['tabs--mobile']} flex-column`}
                            linkClassName={style['tabs__link']}
                            activeClassName={style['tabs__link--active']}
                            items={tabItems ? tabItems : getCategoryTabItems()}
                        />
                    ) : null}
                </ModalBase>
            ) : null}
            {showSubpagesMenu &&
                ((subpages && subpages.length) || (tabItems && tabItems.length)) ? (
                <section {...className('d-none d-lg-block')}>
                    <Container
                        pt={tabSectionPt ? tabSectionPt : { xs: 10 }}
                        pb={{ xs: 5 }}
                    >
                        <Row>
                            <Col
                                data-sal="slide-up"
                                data-sal-easing="ease-out-quart"
                                data-sal-delay={
                                    extraContent ? '' : 'xs-none--lg-900'
                                }
                            >
                                <TabComponent
                                    className={
                                        tabClassName ? tabClassName : style.tabs
                                    }
                                    linkClassName={
                                        tabLinkClassName
                                            ? tabLinkClassName
                                            : style['tabs__link']
                                    }
                                    activeClassName={
                                        tabLinkActiveClassName
                                            ? tabLinkActiveClassName
                                            : style['tabs__link--active']
                                    }
                                    items={
                                        tabItems
                                            ? tabItems
                                            : getCategoryTabItems()
                                    }
                                />
                            </Col>
                        </Row>
                    </Container>
                </section>
            ) : null}
        </>
    );
}
